/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for auto-saving form data
 * @param {Object} initialData - Initial form data
 * @param {string} storageKey - Local storage key for saving data
 * @param {number} delay - Delay in milliseconds before saving (default: 1000)
 * @param {Function} onSave - Optional callback when data is saved (e.g., backend save function)
 * @param {boolean} enablePeriodicSave - Enable periodic auto-save every 2 minutes (default: false)
 * @returns {Object} - { formData, setFormData, isSaving, saveStatus, clearSavedData }
 */
export const useAutoSave = (initialData = {}, storageKey, delay = 1000, onSave = null, enablePeriodicSave = false) => {
  const [formData, setFormData] = useState(initialData);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [lastBackendSaveTime, setLastBackendSaveTime] = useState(null);
  const timeoutRef = useRef(null);
  const periodicIntervalRef = useRef(null);
  const backendSaveIntervalRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Load saved data on mount
  useEffect(() => {
    if (storageKey && isInitialLoad.current) {
      try {
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          console.log('📂 Loading saved form data:', parsedData);
          setFormData(prevData => ({ ...prevData, ...parsedData }));
          setSaveStatus('loaded');
        }
      } catch (error) {
        console.error('Error loading saved form data:', error);
      }
      isInitialLoad.current = false;
    }
  }, [storageKey]);

  // Save data before page unload/refresh
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (storageKey && formData) {
        console.log('💾 Saving form data before page unload...');
        try {
          const dataToSave = {
            ...formData,
            _lastSaved: new Date().toISOString(),
            _savedOnUnload: true
          };
          localStorage.setItem(storageKey, JSON.stringify(dataToSave));
          console.log('✅ Form data saved before unload');
        } catch (error) {
          console.error('Error saving form data before unload:', error);
        }
      }
    };

    // Add event listener for beforeunload
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [storageKey, formData]);

  // Save form data function
  const saveFormData = useCallback(async () => {
    if (!storageKey) return;
    
    setIsSaving(true);
    setSaveStatus('saving');
    
    try {
      // Deep clone form data to avoid reference issues
      const dataToSave = JSON.parse(JSON.stringify({
        ...formData,
        _lastSaved: new Date().toISOString(),
        _version: '1.0'
      }));
      
      // Save to localStorage with error handling
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      
      // Verify the save was successful
      const savedData = localStorage.getItem(storageKey);
      if (!savedData) {
        throw new Error('Failed to save data to localStorage');
      }
      
      // Optional: Save to backend
      if (onSave && typeof onSave === 'function') {
        await onSave(formData);
      }
      
      const saveTime = new Date();
      setLastSaveTime(saveTime);
      setSaveStatus('saved');
      console.log(`✅ Form auto-saved at ${saveTime.toLocaleTimeString()}`, {
        fieldsCount: Object.keys(formData).length,
        hasArrays: Object.values(formData).some(val => Array.isArray(val)),
        storageSize: savedData.length
      });
      
      setTimeout(() => setSaveStatus(null), 3000); // Clear status after 3 seconds
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000); // Clear error status after 4 seconds
    } finally {
      setIsSaving(false);
    }
  }, [formData, storageKey, onSave]);

  // Auto-save effect (debounced)
  useEffect(() => {
    if (!storageKey || isInitialLoad.current) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      saveFormData();
    }, delay);

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [formData, delay, storageKey, saveFormData]);

  // Periodic auto-save effect (every 2 minutes) - localStorage only
  useEffect(() => {
    if (!enablePeriodicSave || !storageKey || isInitialLoad.current) return;

    // Set up periodic save interval (2 minutes = 120000ms)
    periodicIntervalRef.current = setInterval(() => {
      console.log('🔄 Auto-saving form data to localStorage (2-minute interval)...');
      saveFormData();
    }, 120000); // 2 minutes

    // Cleanup interval on unmount
    return () => {
      if (periodicIntervalRef.current) {
        clearInterval(periodicIntervalRef.current);
      }
    };
  }, [enablePeriodicSave, storageKey, saveFormData]);

  // Periodic backend save effect (every 5 minutes) - calls onSave if provided
  useEffect(() => {
    if (!enablePeriodicSave || !onSave || typeof onSave !== 'function' || isInitialLoad.current) return;

    // Set up periodic backend save interval (5 minutes = 300000ms)
    backendSaveIntervalRef.current = setInterval(async () => {
      console.log('🔄 Auto-saving form data to backend (5-minute interval)...');
      setIsSaving(true);
      setSaveStatus('saving-backend');
      try {
        await onSave(formData);
        const saveTime = new Date();
        setLastBackendSaveTime(saveTime);
        setSaveStatus('saved-backend');
        console.log(`✅ Form auto-saved to backend at ${saveTime.toLocaleTimeString()}`);
        setTimeout(() => setSaveStatus(null), 3000);
      } catch (error) {
        console.error('Error in periodic backend save:', error);
        setSaveStatus('error-backend');
        setTimeout(() => setSaveStatus(null), 4000);
      } finally {
        setIsSaving(false);
      }
    }, 300000); // 5 minutes

    // Cleanup interval on unmount
    return () => {
      if (backendSaveIntervalRef.current) {
        clearInterval(backendSaveIntervalRef.current);
      }
    };
  }, [enablePeriodicSave, onSave, formData]);


  // Manual save function (returns promise so callers can await before backend-only saves)
  const manualSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    return saveFormData();
  }, [saveFormData]);

  // Clear saved data function
  const clearSavedData = useCallback(() => {
    if (storageKey) {
      localStorage.removeItem(storageKey);
      setFormData(initialData);
      setSaveStatus('cleared');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  }, [storageKey, initialData]);

  // Force save function for immediate save (returns promise so callers can await)
  const forceSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    return saveFormData();
  }, [saveFormData]);

  // Check if data has been saved recently
  const hasRecentSave = useCallback(() => {
    if (!lastSaveTime) return false;
    const now = new Date();
    const timeDiff = now.getTime() - lastSaveTime.getTime();
    return timeDiff < 5000; // Less than 5 seconds ago
  }, [lastSaveTime]);

  // Update form data function that supports both patterns
  const updateFormData = useCallback((updatesOrFunction) => {
    if (typeof updatesOrFunction === 'function') {
      // Support updater function pattern: setFormData(prev => ({...prev, ...updates}))
      setFormData(updatesOrFunction);
    } else {
      // Support object pattern: setFormData({field: value})
      setFormData(prevData => ({
        ...prevData,
        ...updatesOrFunction
      }));
    }
  }, []);

  return {
    formData,
    setFormData: updateFormData,
    isSaving,
    saveStatus,
    lastSaveTime,
    lastBackendSaveTime,
    manualSave,
    forceSave,
    hasRecentSave,
    clearSavedData
  };
};

/**
 * Hook for auto-filling forms with user registration data
 * @param {Object} userData - User data from registration/profile
 * @param {Object} fieldMapping - Mapping of user data fields to form fields
 * @returns {Object} - Auto-filled form data
 */
export const useAutoFill = (userData = {}, fieldMapping = {}) => {
  const [autoFilledData, setAutoFilledData] = useState({});

  useEffect(() => {
    if (userData && Object.keys(fieldMapping).length > 0) {
      const filled = {};
      
      Object.entries(fieldMapping).forEach(([formField, userField]) => {
        if (userData[userField] !== undefined && userData[userField] !== null) {
          filled[formField] = userData[userField];
        }
      });
      
      setAutoFilledData(filled);
    }
  }, [userData, fieldMapping]);

  return autoFilledData;
};

/**
 * Combined hook for auto-save and auto-fill functionality
 * @param {Object} initialData - Initial form data
 * @param {string} storageKey - Storage key for auto-save
 * @param {Object} userData - User data for auto-fill
 * @param {Object} fieldMapping - Field mapping for auto-fill
 * @param {Function} onSave - Save callback
 * @param {number} delay - Auto-save delay
 * @param {boolean} enablePeriodicSave - Enable periodic auto-save every 2 minutes
 * @returns {Object} - Combined form management object
 */
export const useFormManagement = (initialData = {}, storageKey, userData = {}, fieldMapping = {}, onSave = null, delay = 1000, enablePeriodicSave = false) => {
  // Get auto-filled data
  const autoFilledData = useAutoFill(userData, fieldMapping);
  
  // Combine initial data with auto-filled data
  const combinedInitialData = { ...initialData, ...autoFilledData };
  
  // Use auto-save hook with periodic save
  const autoSaveHook = useAutoSave(combinedInitialData, storageKey, delay, onSave, enablePeriodicSave);

  return {
    ...autoSaveHook,
    autoFilledData,
    hasAutoFilledData: Object.keys(autoFilledData).length > 0
  };
};

export default useAutoSave;
